import { UserPreferenceContext } from '@contexts/user-preference-context';
import { UserPreference } from '@models/user-preferences.model';
import { Status } from '@modules/test-development/enums/status-constant.enum';
import BulkActionService from '@modules/test-development/services/bulk-action.service';
import {
  Modal,
  Spinner,
  useInternalEvents,
  useTranslation,
} from '@testsigma/atoms';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cloneDeep } from 'es-toolkit/object';
import { EntityType } from '@modules/test-development/enums/entity-type.enum';
import { BulkActionType } from '@modules/test-development/enums/bulk-action-type.enum';
import { logErrorToSentry, PageLoader } from '@utils/common';

type BulkActionLoaderProps = {
  id: number;
};

const BulkActionLoader: React.FC<BulkActionLoaderProps> = ({ id }) => {
  const { userPreference, updatePreferenceContext } = useContext(
    UserPreferenceContext,
  );
  const { t } = useTranslation();
  const location = useLocation();
  const [entity, setEntity] = useState(null);
  const [actionType, setActionType] = useState<BulkActionType>(null);
  const intervalRef = useRef(null);
  const publishEvent = useInternalEvents();
  const fetchBulkActionStatus = () => {
    if (id) {
      BulkActionService.ping(id)
        .then((res) => {
          if (res.status === Status.SUCCESS || res.status === Status.FAILURE) {
            clearInterval(intervalRef?.current);
            localStorage.removeItem('bulkActionId');
            localStorage.removeItem('isBulkActionOngoing');
            const modifiedUserPreference: UserPreference =
              cloneDeep(userPreference);
            modifiedUserPreference.isBulkActionOngoing = false;
            modifiedUserPreference.bulkActionId = null;
            updatePreferenceContext(modifiedUserPreference);
            publishEvent('refresh-list');
          }
          setActionType(res.actionType);
          setEntity(res.entityType);
        })
        .catch((err) => logErrorToSentry({ error: err }));
    }
  };
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      fetchBulkActionStatus();
    }, 4000);
    return () => clearInterval(intervalRef?.current);
  }, []);

  return (
    <Modal
      isOpen={true}
      className='flex flex-col justify-center items-center rounded-xl w-96 h-60 p-4'
    >
      {!!entity ? (
        <>
          <h1 className='text-lg text-secondary-unique-600 font-bold mb-6'>
            {actionType == BulkActionType.DELETE ||
            actionType == BulkActionType.SOFT_DELETE
              ? t('bulk_delete.loader.delete_entity', {
                  entity: `${
                    entity == EntityType.TEST_CASE ? 'Test cases' : 'Elements'
                  }`,
                })
              : t('bulk_update.loader.updating_entity', {
                  entity: `${
                    entity == EntityType.TEST_CASE ? 'Test cases' : 'Elements'
                  }`,
                })}
          </h1>
          <Spinner
            thickness={60}
            speed={140}
            width={64}
            height={64}
            color='#03A973'
            secondaryColor='rgba(172, 57, 57, 0)'
          />
          <h3 className='text-xs text-secondary-unique-800 mt-6 text-center w-[250px]'>
            {actionType == BulkActionType.DELETE ||
            actionType == BulkActionType.SOFT_DELETE
              ? t('bulk_delete.please_wait')
              : t('bulk_update.please_wait')}
          </h3>
        </>
      ) : (
        <Spinner
          thickness={60}
          speed={140}
          width={64}
          height={64}
          color='#03A973'
          secondaryColor='rgba(172, 57, 57, 0)'
        />
      )}
    </Modal>
  );
};

export default BulkActionLoader;
